/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import Vue from 'vue';
import Vuex, { ActionContext } from 'vuex';
import VueRouter from 'vue-router';
import createLogger from 'vuex/dist/logger';
import { forEach, filter, find, get } from 'lodash';

import aswService from '@/pages/audit_setup_wizard/asw_service';
import createAlertsStore from '@/components/global_alerts/store';
import createExportStore from '@/pages/export/store';
import createModalsStore from '@/components/zen_modal/store';
import createSurveyBuilder from '@/state/modules/survey_builder';
import createTodoListStore from '@/state/modules/todo_list/state';
import createWorkflowsStore from '@/state/modules/workflows/workflowsState';
import createHeatmapStore from '@/state/modules/heatmap/state';
import surveyBuilderService from '@/pages/survey_builder/service';
import { loadSiteConfig } from '@/utils/crudActions';
import { SiteConfigResponseSchema, ModelMetaSchema } from '@/types/apiTypes';
import { keysToSnake, keysToCamel } from '@/utils';

import createAuditSetupWizard from './modules/audit_setup_wizard';
import { PageBackLink, RequestParams, RootState } from './types';
import { sendRequest } from '@/utils/client/client';

const debug = process.env.NODE_ENV !== 'production';

Vue.use(Vuex);

export function createStore(router: VueRouter) {
  return new Vuex.Store({
    modules: {
      auditSetupWizard: createAuditSetupWizard(aswService, router),
      surveyBuilder: createSurveyBuilder(surveyBuilderService),
      todoList: createTodoListStore(),
      workflows: createWorkflowsStore(),
      heatmap: createHeatmapStore(),
      export: createExportStore(),
      modals: createModalsStore(),
      alerts: createAlertsStore(),
    },
    state: {
      appVersion: '',
      appVersionHash: '',
      companyId: '',
      companyName: '',
      csrfToken: '',
      featureFlags: {},
      currentUser: {},
      globalSettings: {},
      lhn: { newMenu: [], menuEntries: [] },
      modelMeta: {},
      privacyPolicy: '',
      routes: {},
      segmentBrowserKey: '',
      intercomAppId: '',
      zendeskEnabled: false,
      ZendeskSubdomain: '',
      availableMapTypes: [],
      routeLoadCount: 0,
      // Everything above this comment is initialized from server
      pageTitle: '',
      pageTitleVisible: true,
      pageTitleBackLink: false,
      pageTitleIsBeta: false,
      pageTitleIsNew: false,
      initialized: false,
    },
    getters: {
      csrfToken(state: RootState) {
        return state.globalSettings.csrfToken;
      },
      featureFlags(state: RootState) {
        return state.featureFlags;
      },
      featureFlagState(state: RootState): (flagKey: string) => boolean {
        return flagKey => get(state.featureFlags, flagKey, false);
      },
      lhnData(state: RootState) {
        return {
          info: {
            zendeskEnabled: state.globalSettings.zendeskEnabled,
            user: state.currentUser,
            privacyPolicy: state.globalSettings.privacyPolicy,
            appVersion: state.globalSettings.appVersion,
            appVersionHash: state.globalSettings.appVersionHash,
          },
          newMenu: state.lhn.newMenu,
          navLinks: state.lhn.menuEntries,
        };
      },
      pageTitle(state: RootState) {
        return state.pageTitle;
      },
      pageTitleVisible(state: RootState) {
        return state.pageTitleVisible;
      },
      pageTitleBackLink(state: RootState) {
        return state.pageTitleBackLink;
      },
      pageTitleIsNew(state: RootState) {
        return state.pageTitleIsNew;
      },
      pageTitleIsBeta(state: RootState) {
        return state.pageTitleIsBeta;
      },
      routes(state: RootState) {
        return state.routes;
      },
      pageExtensions(state: RootState) {
        return state.pageExtensions;
      },
      exportEndpoints(state: RootState) {
        return {
          config: state.routes.exportConfig,
          block: state.routes.exportBlock,
          filter: state.routes.exportFilter,
          saveConfig: state.routes.exportSaveConfig,
          deleteConfig: state.routes.exportDeleteConfig,
          generateReport: state.routes.exportGenerateReport,
        };
      },
      importableObjects(state: RootState) {
        return filter(state.modelMeta, model => model.importable);
      },
      exportableObjects(state: RootState) {
        return filter(state.modelMeta, model => model.exportable);
      },
      route(state: RootState) {
        return state.route;
      },
      objectMapperApiEndpoints(state: RootState) {
        return {
          mappedObjects: state.routes.mapperMappedObjects,
          relevantFilter: state.routes.mapperRelevantFilter,
        };
      },
      auditSetupWizardApiEndpoints(state: RootState) {
        return {
          relatedPrograms: state.routes.aswRelatedPrograms,
          auditManagers: state.routes.aswAuditManagers,
          auditors: state.routes.aswAuditors,
        };
      },
      surveySendingApiEndpoints(state: RootState) {
        return {
          surveyEmailTemplateList: state.routes.surveyEmailTemplateList,
        };
      },
      availableMapTypes(state: RootState) {
        return state.availableMapTypes;
      },
      isUserLoggedIn(state: RootState) {
        return state.currentUser && state.currentUser.dbId;
      },
      currentUser(state: RootState) {
        return state.currentUser;
      },
      modelMeta(state: RootState) {
        return state.modelMeta;
      },
      modelMetaFindBy(
        state: RootState
      ): (metaKey: keyof ModelMetaSchema, metaValue: string) => ModelMetaSchema | undefined {
        return (metaKey, metaValue) => {
          return find(state.modelMeta, modelMeta => modelMeta[metaKey] === metaValue);
        };
      },
      companyInfo(state: RootState) {
        return {
          companyId: state.companyId,
          companyName: state.companyName,
        };
      },
      externalServicesInfo(state: RootState) {
        return {
          segmentBrowserKey: state.globalSettings.segmentBrowserKey,
          intercomAppId: state.globalSettings.intercomAppId,
          zendeskEnabled: state.globalSettings.zendeskEnabled,
          zendeskSubdomain: state.globalSettings.zendeskSubdomain,
        };
      },
      isInstantEmailsOn(state: RootState) {
        return state.globalSettings.instantEmails;
      },
      routeLoadCount(state: RootState) {
        return state.routeLoadCount;
      },
    },
    actions: {
      setPageTitle(
        { commit, state }: ActionContext,
        { title = '', visible = true, backLink = false, isBeta = false, isNew = false }
      ) {
        document.title = `${title} | ${state.companyName}`;
        commit('SET_PAGE_TITLE', title);
        commit('SET_PAGE_TITLE_VISIBLE', visible);
        commit('SET_PAGE_TITLE_BACK_LINK', backLink);
        commit('SET_PAGE_TITLE_BETA', isBeta);
        commit('SET_PAGE_TITLE_NEW', isNew);
      },
      setInstantEmails({ commit }: ActionContext, { instantEmailsOn = false }) {
        commit('SET_INSTANT_EMAILS', instantEmailsOn);
      },
      initialize({ commit }: ActionContext) {
        return loadSiteConfig()
          .then((response: SiteConfigResponseSchema) => {
            commit('SET_INITIAL_CONFIG', response);
          })
          .then(() => {
            commit('SET_INITALIZED');
          });
      },
      redirect(_: RootState, url: string) {
        window.location.href = url;
      },
      request(
        { state, dispatch }: ActionContext<RootState, RootState>,
        { method = 'get', route, url, params, data, raw = false, defaultErrorHandler = true }: RequestParams
      ) {
        let resolver;
        if (raw) {
          resolver = response => response;
        }
        let request = sendRequest(
          {
            method,
            url: url || state.routes[route],
            headers: {
              'X-CSRF-Token': state.csrfToken,
              ...(data ? { 'content-type': 'application/json' } : {}),
            },
            ...(data ? { data: JSON.stringify(keysToSnake(data)) } : {}),
            ...(params ? { params: keysToSnake(params) } : {}),
            transformResponse: response => keysToCamel(JSON.parse(response)),
          },
          resolver
        );

        if (defaultErrorHandler) {
          request.catch(() => {
            dispatch('alerts/show', { type: 'error', message: 'Oops, something went wrong.' });
          });
        }

        return request;
      },
    },
    mutations: {
      SET_PAGE_TITLE(state: RootState, pageTitle: string) {
        state.pageTitle = pageTitle;
      },
      SET_PAGE_TITLE_VISIBLE(state: RootState, pageTitleVisible: boolean) {
        state.pageTitleVisible = pageTitleVisible;
      },
      SET_PAGE_TITLE_BACK_LINK(state: RootState, backLink: PageBackLink | false) {
        state.pageTitleBackLink = backLink;
      },
      SET_PAGE_TITLE_BETA(state: RootState, isBeta: boolean | false) {
        state.pageTitleIsBeta = isBeta;
      },
      SET_PAGE_TITLE_NEW(state: RootState, isNew: boolean | false) {
        state.pageTitleIsNew = isNew;
      },
      SET_INITALIZED(state: RootState) {
        state.initialized = true;
      },
      SET_INITIAL_CONFIG(state: RootState, siteConfig: SiteConfigResponseSchema) {
        forEach(siteConfig, (value, key) => {
          state[key] = value;
        });
      },
      SET_INSTANT_EMAILS(state: RootState, instantEmailsOn: boolean) {
        state.globalSettings.instantEmails = instantEmailsOn;
      },
      SET_ROUTE_LOAD_COUNT(state: RootState, count: number) {
        state.routeLoadCount = count;
      },
    },
    strict: debug,
    plugins: debug ? [createLogger()] : [],
  });
}
